import React, { useState, useRef, useLayoutEffect } from "react";
import { PartyPopper, Gift } from "lucide-react";
import { LAUNCH_DATE } from "@/components/LaunchBanner";

const SPEED_PX_PER_SEC = 150; // constant glide speed → duration scales with screen width
const MIN_DURATION = 7; // never faster than this (keeps small screens calm too)
const DAY_MS = 24 * 60 * 60 * 1000;

const BANNERS = [
  {
    id: "live",
    icon: PartyPopper,
    gradient: "from-violet-600 via-fuchsia-600 to-emerald-500",
    content: (
      <>
        We're officially&nbsp;<span className="font-black">LIVE</span>&nbsp;as of {LAUNCH_DATE}! Grand-launch sale on all Core Credits, grow with us!
      </>
    ),
  },
  {
    id: "refer",
    icon: Gift,
    gradient: "from-emerald-500 via-emerald-400 to-teal-500",
    content: (
      <>
        Refer a company and get&nbsp;<span className="font-black">free credits</span>&nbsp;, you both earn 100 Core Credits when they join!
      </>
    ),
  },
];

function shouldPlay() {
  try {
    const playedSession = sessionStorage.getItem("promo_banners_session") === "1";
    const last = Number(localStorage.getItem("promo_banners_last") || 0);
    const dayPassed = Date.now() - last > DAY_MS;
    return !playedSession || dayPassed;
  } catch {
    return true;
  }
}

export default function PromoBanners() {
  const [index, setIndex] = useState(() => {
    if (!shouldPlay()) return -1;
    // Mark as played immediately so a page refresh mid-sequence won't restart it.
    try {
      sessionStorage.setItem("promo_banners_session", "1");
      localStorage.setItem("promo_banners_last", String(Date.now()));
    } catch {
      /* storage may be blocked */
    }
    return 0;
  });

  if (index < 0 || index >= BANNERS.length) return null;

  const banner = BANNERS[index];

  return <BannerBar key={banner.id} banner={banner} onDone={() => setIndex((i) => i + 1)} />;
}

function BannerBar({ banner, onDone }) {
  const barRef = useRef(null);
  const [dur, setDur] = useState(0);
  const Icon = banner.icon;

  useLayoutEffect(() => {
    const w = barRef.current?.offsetWidth || 600;
    const distance = window.innerWidth + w; // full travel: off-right → off-left
    setDur(Math.max(MIN_DURATION, distance / SPEED_PX_PER_SEC));
  }, []);

  return (
    <div
      data-testid="promo-banners"
      className="relative z-40 h-12 sm:h-11 overflow-hidden select-none"
    >
      <style
        dangerouslySetInnerHTML={{
          __html:
            "@keyframes promo-slide{from{transform:translateX(100vw)}to{transform:translateX(-100%)}}",
        }}
      />
      <div
        ref={barRef}
        data-testid={`promo-banner-${banner.id}`}
        onAnimationEnd={onDone}
        className={`absolute top-0 left-0 h-full w-max flex items-center gap-2.5 px-7 text-white text-sm sm:text-base font-bold whitespace-nowrap bg-gradient-to-r ${banner.gradient} pointer-events-none will-change-transform`}
        style={{
          transform: "translateX(100vw)",
          animation: dur ? `promo-slide ${dur}s linear forwards` : "none",
        }}
      >
        <Icon className="w-5 h-5 shrink-0" />
        <span>{banner.content}</span>
      </div>
    </div>
  );
}
